import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { Main as Container } from 'Components/Main';
import { PageHelper } from 'Components/PageHelper';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { getAssetsShortPaged } from 'services/assets-service';
import { getUsersName } from 'services/users-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { Dialog, PageHeader, Select, TextField } from 'Components/ui';
import { callArchiveAction, callDeleteAction, callUnarchiveAction } from './actions';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';

/* ─── Page Header ─── */
const AddBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ─── Info Banner ─── */
const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;
const InfoBannerTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 4px;
`;
const InfoBannerDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

/* ─── Filter Card ─── */
const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const FilterSectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 16px;
`;
const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
`;
const FilterGrid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  align-items: end;
`;
const FilterLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
  margin: 0 0 5px;
`;
const ClearBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const FilterBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ─── Table ─── */
const TableWrapper = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
  margin-bottom: 16px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Thead = styled.thead`
  background: ${BG};
`;
const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid ${DIVIDER};
  white-space: nowrap;
`;
const ThCenter = styled(Th)`
  text-align: center;
`;
const Tr = styled.tr`
  border-bottom: 1px solid ${BG};
  cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`;
const Td = styled.td`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  padding: 12px 16px;
  vertical-align: middle;
`;
const TdBold = styled(Td)`
  font-size: 14px;
  font-weight: 500;
  color: ${INK};
`;
const TdSm = styled(Td)`
  color: ${MUTED};
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ActionCell = styled.td`
  padding: 8px 16px;
  text-align: center;
`;
const ActionsWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

/* ─── Badges ─── */
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  background: ${p => p.bg};
  color: ${p => p.color};
`;

const CRIT_COLORS = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};
const TYPE_COLORS = {
  'Pessoa': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  'Tecnologia': { color: 'var(--vo-cobalto)', bg: 'color-mix(in srgb, var(--vo-cobalto) 10%, transparent)' },
  'Ambiente': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
};

/* ─── Action Buttons ─── */
const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${MUTED};
  &:hover {
    background: ${p => p.danger ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${p => p.danger ? 'var(--vo-danger)' : BLUE};
  }
`;

/* ─── Pagination ─── */
const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${BG};
`;
const PaginationCount = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const PageBtns = styled.div`
  display: flex;
  gap: 4px;
`;
const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border: ${p => p.active ? 'none' : `1px solid ${BORDER}`};
  background: ${p => p.active ? BLUE : 'white'};
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${p => p.active ? 700 : 400};
  color: ${p => p.active ? 'white' : MUTED};
  cursor: ${p => p.active ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: ${p => p.active ? BLUE : BG}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

/* ─── SVG Icons ─── */
const PlusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const EyeSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EditSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const ArchiveSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);
const TrashSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const SearchSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const ChevronSvg = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {dir === 'left' ? <polyline points="15 18 9 12 15 6"/> : <polyline points="9 18 15 12 9 6"/>}
  </svg>
);

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
};

export const Assets = () => {
  const { t } = useTranslation(['asm', 'common']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, getValues } = useForm({
    defaultValues: { name: '', criticity: '', type: '', user: '', status: 'active', pii: 'Todos' },
  });

  const getContentSearch = () => {
    const dados = getValues();
    let queryString = `&pii=${dados.pii}`;
    if (dados.status === 'archived') queryString += `&archived=true`;
    else queryString += `&archived=false`;
    if (dados.name !== '') queryString += `&name=${dados.name}`;
    if (dados.criticity !== '') queryString += `&criticity=${dados.criticity}`;
    if (dados.type !== '') queryString += `&type=${dados.type}`;
    if (dados.user !== '') queryString += `&user=${dados.user}`;
    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { isLoading, error, data: pagedAssets } = useQuery(['getAssets', refetch, page], async () => {
    if (!selectedTenant.slug) return { data: [], page, totalPages: 0 };
    const queryContent = getContentSearch();
    const result = await getAssetsShortPaged(selectedTenant.slug, page, 15, queryContent);
    if (result?.data.data.length > 0) return result?.data;
    return { data: [], page, totalPages: 0 };
  });

  const onDelete = item => { setItemToDelete(item); setOpenDeleteModal(true); };
  const onArchive = item => { setItemToArchive(item); setOpenArchiveModal(true); };
  const onUnarchive = item => { setItemToUnarchive(item); setOpenUnarchiveModal(true); };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, () => { setOpenDeleteModal(false); setItemToDelete(null); }, refetch, setRefetch);
    } catch (err) {
      setOpenDeleteModal(false); setItemToDelete(null);
      Dialog.alert({ title: t('asm:assets.delete.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };
  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, () => { setOpenArchiveModal(false); setItemToArchive(null); }, refetch, setRefetch);
    } catch (err) {
      setOpenArchiveModal(false); setItemToArchive(null);
      Dialog.alert({ title: t('asm:assets.delete.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };
  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, () => { setOpenUnarchiveModal(false); setItemToUnarchive(null); }, refetch, setRefetch);
    } catch (err) {
      setOpenUnarchiveModal(false); setItemToUnarchive(null);
      Dialog.alert({ title: t('asm:assets.delete.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const handleFilter = () => { setPage(1); setRefetch(!refetch); };
  const handleClear = () => { reset(); setPage(1); setRefetch(!refetch); };

  const PAGE_SIZE = 15;
  const totalPages = pagedAssets?.totalPages ?? 0;
  const currentCount = pagedAssets?.data?.length || 0;
  const totalItems = pagedAssets?.total ?? (totalPages <= 1 ? currentCount : totalPages * PAGE_SIZE);
  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <Container>
      <PageHeader
        title={t('asm:assets.title')}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('asm:assets.title') },
        ]}
        actions={
          <AddBtn onClick={() => navigate('/assets/add')}>
            <PlusSvg />
            {t('asm:assets.addButton')}
          </AddBtn>
        }
      />

      <InfoBanner>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        </svg>
        <div>
          <InfoBannerTitle>{t('asm:assets.infoCardTitle')}</InfoBannerTitle>
          <InfoBannerDesc>{t('asm:assets.infoDescription')}</InfoBannerDesc>
        </div>
      </InfoBanner>

      <FilterCard>
        <FilterSectionLabel>{t('asm:assets.filter.sectionTitle')}</FilterSectionLabel>
        <FilterGrid>
          <div>
            <FilterLabel>{t('asm:assets.filter.name')}</FilterLabel>
            <TextField fullWidth {...register('name')} placeholder={t('asm:assets.filter.namePlaceholder')} />
          </div>
          <div>
            <FilterLabel>{t('asm:assets.filter.criticality')}</FilterLabel>
            <Controller
              control={control}
              name="criticity"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="criticity"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('asm:assets.filter.criticalityPlaceholder') },
                    ...['1','2','3','4','5'].map(item => ({ value: item, label: t(`asm:criticality.${item}`) })),
                  ]}
                />
              )}
            />
          </div>
          <div>
            <FilterLabel>{t('asm:assets.filter.type')}</FilterLabel>
            <Controller
              control={control}
              name="type"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="type"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('asm:assets.filter.typePlaceholder') },
                    { value: 'Pessoa', label: t('asm:assets.filter.typePerson') },
                    { value: 'Tecnologia', label: t('asm:assets.filter.typeTechnology') },
                    { value: 'Ambiente', label: t('asm:assets.filter.typeEnvironment') },
                  ]}
                />
              )}
            />
          </div>
          <div>
            <FilterLabel>{t('asm:assets.filter.pii')}</FilterLabel>
            <Controller
              control={control}
              name="pii"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="pii"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: 'Todos', label: t('asm:assets.filter.piiAll') },
                    { value: 'Sim', label: t('asm:assets.filter.piiYes') },
                    { value: 'Não', label: t('asm:assets.filter.piiNo') },
                  ]}
                />
              )}
            />
          </div>
        </FilterGrid>
        <FilterGrid2>
          <div>
            <FilterLabel>{t('asm:assets.filter.responsible')}</FilterLabel>
            <Controller
              control={control}
              name="user"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="user"
                  value={value}
                  onChange={onChange}
                  disabled={userLoading}
                  options={[
                    { value: '', label: t('asm:assets.filter.responsiblePlaceholder') },
                    ...(users || []).map(u => ({ value: u.name, label: u.name })),
                  ]}
                />
              )}
            />
          </div>
          <div>
            <FilterLabel>{t('asm:assets.filter.status')}</FilterLabel>
            <Controller
              control={control}
              name="status"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="status"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: 'all', label: t('asm:assets.filter.statusAll') },
                    { value: 'active', label: t('asm:assets.filter.statusActive') },
                    { value: 'archived', label: t('asm:assets.filter.statusArchived') },
                  ]}
                />
              )}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <ClearBtn type="button" onClick={handleClear}>{t('asm:assets.filter.clear')}</ClearBtn>
            <FilterBtn type="button" onClick={handleFilter}>
              <SearchSvg />
              {t('asm:assets.filter.filter')}
            </FilterBtn>
          </div>
        </FilterGrid2>
      </FilterCard>

      {isLoading && <PageLoaderList />}
      {error && <div style={{ color: 'var(--vo-danger)', padding: '16px 0' }}>{t('asm:assets.error')}</div>}

      {!isLoading && !error && pagedAssets?.data?.length === 0 && (
        <NoData description={t('asm:assets.noData')} />
      )}

      {!isLoading && !error && pagedAssets?.data?.length > 0 && (
        <>
          <TableWrapper>
            <Table>
              <Thead>
                <tr>
                  <Th>{t('asm:assets.table.name')}</Th>
                  <Th>{t('asm:assets.table.criticality')}</Th>
                  <Th>{t('asm:assets.table.type')}</Th>
                  <Th>{t('asm:assets.table.pii')}</Th>
                  <Th>{t('asm:assets.table.location')}</Th>
                  <Th>{t('asm:assets.table.responsible')}</Th>
                  <ThCenter>{t('asm:assets.table.actions')}</ThCenter>
                </tr>
              </Thead>
              <tbody>
                {pagedAssets.data.map(asset => {
                  const crit = CRIT_COLORS[asset.criticity];
                  const typeStyle = TYPE_COLORS[asset.type];
                  return (
                    <Tr key={asset._id} onClick={() => navigate('/assets/show/' + asset._id)}>
                      <TdBold>{asset.name}</TdBold>
                      <Td>
                        {crit
                          ? <Badge bg={crit.bg} color={crit.color}>{t(`asm:criticality.short.${asset.criticity}`)}</Badge>
                          : <span style={{ color: MUTED }}>-</span>
                        }
                      </Td>
                      <Td>
                        {typeStyle
                          ? <Badge bg={typeStyle.bg} color={typeStyle.color}>{asset.type}</Badge>
                          : <span style={{ color: MUTED }}>{asset.type || '-'}</span>
                        }
                      </Td>
                      <Td>
                        {asset.pii
                          ? <Badge bg="var(--vo-danger-10)" color="var(--vo-danger)">{t('asm:assets.filter.piiYes')}</Badge>
                          : <Badge bg="var(--vo-slate-10)" color={MUTED}>{t('asm:assets.filter.piiNo')}</Badge>
                        }
                      </Td>
                      <TdSm title={asset.location}>{asset.location || '-'}</TdSm>
                      <Td>{asset.user || '-'}</Td>
                      <ActionCell onClick={e => e.stopPropagation()}>
                        <ActionsWrap>
                          <IconBtn title={t('asm:assets.show.edit')} onClick={() => navigate('/assets/show/' + asset._id)}>
                            <EyeSvg />
                          </IconBtn>
                          <IconBtn title={t('asm:assets.show.edit')} onClick={() => navigate('/assets/edit/' + asset._id)}>
                            <EditSvg />
                          </IconBtn>
                          <IconBtn
                            title={asset.archived ? t('asm:assets.delete.unarchive') : t('asm:assets.delete.archive')}
                            onClick={() => asset.archived ? onUnarchive(asset) : onArchive(asset)}
                          >
                            <ArchiveSvg />
                          </IconBtn>
                          <IconBtn danger title={t('asm:assets.delete.confirm')} onClick={() => onDelete(asset)}>
                            <TrashSvg />
                          </IconBtn>
                        </ActionsWrap>
                      </ActionCell>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <TableFooter>
              <PaginationCount>{t('asm:assets.pagination.showing', { count: currentCount, total: totalItems })}</PaginationCount>
              <PageBtns>
                <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronSvg dir="left" />
                </PageBtn>
                {pageNumbers.map((num, i) =>
                  num === '...'
                    ? <span key={`e-${i}`} style={{ padding: '0 4px', color: MUTED, fontFamily: 'Lato', fontSize: 13 }}>…</span>
                    : <PageBtn key={num} active={page === num} onClick={() => setPage(num)}>{num}</PageBtn>
                )}
                <PageBtn disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronSvg dir="right" />
                </PageBtn>
              </PageBtns>
            </TableFooter>
          </TableWrapper>
        </>
      )}

      <Dialog
        open={openDeleteModal}
        onConfirm={onConfirmDeleteModal}
        onCancel={() => { setOpenDeleteModal(false); setItemToDelete(null); }}
        message={`${t('asm:assets.delete.confirm')} ${itemToDelete?.name}?`}
      />
      <Dialog
        open={openArchiveModal}
        onConfirm={onConfirmArchiveModal}
        onCancel={() => { setOpenArchiveModal(false); setItemToArchive(null); }}
        message={`${t('asm:assets.delete.archive')} ${itemToArchive?.name}?`}
      />
      <Dialog
        open={openUnarchiveModal}
        onConfirm={onConfirmUnarchiveModal}
        onCancel={() => { setOpenUnarchiveModal(false); setItemToUnarchive(null); }}
        message={`${t('asm:assets.delete.unarchive')} ${itemToUnarchive?.name}?`}
      />

      <PageHelper steps={tourSteps} tourKey="assets-list" />
    </Container>
  );
};

const tourSteps = [
  { target: 'body', content: 'Bem-vindo à página de Ativos! Aqui você gerencia todos os recursos que suportam suas operações.', disableBeacon: true, disableScrolling: true, placement: 'center' },
];
